import React, { useState, useEffect, useRef, useCallback } from "react"
import {
  Col,
  Card,
  CardBody,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
} from "reactstrap"
import api from "../../services/api"
import Select from "react-select"
import Swal from "sweetalert2"
import Input from "components/shared/Inputs/Inputs"
import InputMask from "react-input-mask"

const SindicosList = () => {
  const [sindicos, setSindicos] = useState([])
  const [search, setSearch] = useState("")
  const [condominios, setCondominios] = useState([])

  // Infinite scroll
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const observer = useRef()

  // Modals
  const [modalVinculo, setModalVinculo] = useState(false)
  const [modalCadastro, setModalCadastro] = useState(false)
  const [modalEditar, setModalEditar] = useState(false)
  const [modalNovaSenha, setModalNovaSenha] = useState(false)

  // States
  const [selectedSindico, setSelectedSindico] = useState(null)
  const [selectedCondos, setSelectedCondos] = useState([])

  const emptySindicoForm = {
    DS_NOME: "",
    DS_SOBRENOME: "",
    NR_TELEFONE: "",
    DS_EMAIL: "",
    CLIENT_SECRET: "",
    CLIENT_SECRET_CONFIRM: "",
    DS_CPF: "",
    DS_STATUS: 1,
  }

  const [novoSindico, setNovoSindico] = useState(emptySindicoForm)
  const [editSindico, setEditSindico] = useState(emptySindicoForm)
  const [senhaForm, setSenhaForm] = useState({
    CLIENT_SECRET: "",
    CLIENT_SECRET_CONFIRM: "",
  })

  const fetchSindicos = useCallback(() => {
    setIsLoadingMore(true)

    let url = `/user/sindicos?page=${page}&limit=${limit}`
    if (search) {
      url += `&search=${search}`
    }

    api
      .get(url)
      .then(res => {
        const newData = res.data.data || []
        const newTotal = res.data.total || 0

        // Se página = 1, substituir. Senão, adicionar (append)
        if (page === 1) {
          setSindicos(newData)
        } else {
          setSindicos(prev => [...prev, ...newData])
        }

        setTotal(newTotal)
        setHasMore(newData.length === limit)
      })
      .catch(err => {
        console.error(err)
        if (page === 1) {
          setSindicos([])
        }
      })
      .finally(() => {
        setIsLoadingMore(false)
      })
  }, [page, limit, search])

  const fetchCondominios = () => {
    api.get("/empresa/").then(response => {
      const options = response.data.map(e => ({
        value: e.OID_EMPRESA,
        label: e.NM_RAZAO_SOCIAL,
        currentSindicoId: e.OID_USUARIO ?? null,
      }))
      setCondominios(options)
    })
  }

  useEffect(() => {
    fetchCondominios()
  }, [])

  useEffect(() => {
    fetchSindicos()
  }, [fetchSindicos])

  const handleSearch = e => {
    if (!e || e.key === "Enter" || e.type === "click") {
      setPage(1)
      setHasMore(true)
    }
  }

  const lastSindicoElementRef = useCallback(
    node => {
      if (isLoadingMore) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(prevPage => prevPage + 1)
        }
      })

      if (node) observer.current.observe(node)
    },
    [isLoadingMore, hasMore],
  )

  const openVinculo = sindico => {
    setSelectedSindico(sindico)

    // Parse current condominios
    let current = []
    if (sindico.condominios) {
      current = sindico.condominios
        .filter(c => c && c.OID_EMPRESA)
        .map(c => ({ value: c.OID_EMPRESA, label: c.NM_RAZAO_SOCIAL }))
    }
    setSelectedCondos(current)
    setModalVinculo(true)
  }

  const openEditar = sindico => {
    setSelectedSindico(sindico)
    setEditSindico({
      DS_NOME: sindico.DS_NOME || "",
      DS_SOBRENOME: sindico.DS_SOBRENOME || "",
      NR_TELEFONE: sindico.NR_TELEFONE || "",
      DS_EMAIL: sindico.DS_EMAIL || "",
      CLIENT_SECRET: "",
      CLIENT_SECRET_CONFIRM: "",
      DS_CPF: sindico.DS_CPF || "",
      DS_STATUS: 1,
    })
    setModalEditar(true)
  }

  const openNovaSenha = () => {
    setSenhaForm({ CLIENT_SECRET: "", CLIENT_SECRET_CONFIRM: "" })
    setModalNovaSenha(true)
  }

  const saveVinculos = async () => {
    try {
      // For each selected condo, we send a PUT to /empresa/:id with OID_USUARIO
      // Note: If we un-select, we'd need to remove OID_USUARIO from those.
      // To keep it simple, let's just do an update for the selected ones.
      // Actually, to properly replace, we might need a custom endpoint, but we can do it with multiple calls.

      const currentIds = selectedSindico.condominios
        ? selectedSindico.condominios
            .filter(c => c && c.OID_EMPRESA)
            .map(c => Number(c.OID_EMPRESA))
        : []
      const newIds = selectedCondos.map(c => Number(c.value))

      const toAdd = newIds.filter(id => !currentIds.includes(id))
      const toRemove = currentIds.filter(id => !newIds.includes(id))

      const conflictingCondos = condominios.filter(
        condo =>
          toAdd.includes(Number(condo.value)) &&
          condo.currentSindicoId !== null &&
          Number(condo.currentSindicoId) !==
            Number(selectedSindico.OID_USUARIO),
      )

      if (conflictingCondos.length > 0) {
        const previewList = conflictingCondos
          .slice(0, 5)
          .map(c => `- ${c.label}`)
          .join("<br>")
        const hasMore = conflictingCondos.length > 5

        const result = await Swal.fire({
          title: "Atenção: síndico já vinculado",
          html: `Os condomínios selecionados já possuem síndico.<br><br>Ao continuar, o síndico atual será <strong>desvinculado</strong> e substituído neste vínculo.<br><br>${previewList}${hasMore ? "<br>..." : ""}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sim, substituir vínculos",
          cancelButtonText: "Cancelar",
        })

        if (!result.isConfirmed) {
          return
        }
      }

      const promises = []
      const sindicoId = Number(selectedSindico.OID_USUARIO)

      for (let id of toAdd) {
        promises.push(
          api.put(`/empresa/${id}/sindico`, {
            OID_USUARIO: sindicoId,
          }),
        )
      }

      for (let id of toRemove) {
        promises.push(api.put(`/empresa/${id}/sindico`, { OID_USUARIO: null }))
      }

      await Promise.all(promises)

      Swal.fire("Sucesso", "Vínculos atualizados com sucesso", "success")
      setModalVinculo(false)
      setPage(1)
      setHasMore(true)
      fetchCondominios()
      fetchSindicos()
    } catch (err) {
      Swal.fire("Erro", "Erro ao atualizar vínculos", "error")
    }
  }

  const saveSindico = () => {
    // Validation: make everything required except CPF and Telefone
    const {
      DS_NOME,
      DS_SOBRENOME,
      DS_EMAIL,
      CLIENT_SECRET,
      CLIENT_SECRET_CONFIRM,
    } = novoSindico

    if (!DS_NOME || !DS_SOBRENOME || !DS_EMAIL || !CLIENT_SECRET) {
      Swal.fire(
        "Atenção",
        "Os campos Nome, Sobrenome, E-mail e Senha são obrigatórios.",
        "warning",
      )
      return
    }

    if (CLIENT_SECRET !== CLIENT_SECRET_CONFIRM) {
      Swal.fire(
        "Atenção",
        "A confirmação de senha deve ser igual à senha.",
        "warning",
      )
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(DS_EMAIL)) {
      Swal.fire("Atenção", "Por favor, insira um e-mail válido.", "warning")
      return
    }

    // Duplicate checks
    const lowerEmail = DS_EMAIL.trim().toLowerCase()
    const hasDuplicateEmail = sindicos.some(
      s => s.DS_EMAIL && s.DS_EMAIL.trim().toLowerCase() === lowerEmail,
    )
    if (hasDuplicateEmail) {
      Swal.fire(
        "Atenção",
        "Já existe um síndico cadastrado com este E-mail.",
        "warning",
      )
      return
    }

    const normalizedCpf = (novoSindico.DS_CPF || "").replace(/\D/g, "")
    const normalizedPhone = (novoSindico.NR_TELEFONE || "").replace(/\D/g, "")

    if (normalizedCpf) {
      const isDuplicateCpf = sindicos.some(
        s => (s.DS_CPF || "").replace(/\D/g, "") === normalizedCpf,
      )
      if (isDuplicateCpf) {
        Swal.fire(
          "Atenção",
          "Já existe um síndico cadastrado com este CPF.",
          "warning",
        )
        return
      }
    }

    if (normalizedPhone) {
      const isDuplicatePhone = sindicos.some(
        s => (s.NR_TELEFONE || "").replace(/\D/g, "") === normalizedPhone,
      )
      if (isDuplicatePhone) {
        Swal.fire(
          "Atenção",
          "Já existe um síndico cadastrado com este Telefone.",
          "warning",
        )
        return
      }
    }

    const { CLIENT_SECRET_CONFIRM: _confirm, ...rest } = novoSindico
    const payload = {
      ...rest,
      DS_EMAIL: (DS_EMAIL || "").trim(),
      CLIENT_SECRET: (CLIENT_SECRET || "").trim(),
      DS_CPF: normalizedCpf || null,
      NR_TELEFONE: normalizedPhone || null,
      DS_STATUS: 1,
    }

    api
      .post("/user/user", payload)
      .then(res => {
        Swal.fire("Sucesso", "Síndico cadastrado com sucesso", "success")
        setModalCadastro(false)
        setPage(1)
        setHasMore(true)
        fetchSindicos()
        setNovoSindico({ ...emptySindicoForm })
      })
      .catch(err => {
        const apiMessage = err?.response?.data?.message
        const text = Array.isArray(apiMessage)
          ? apiMessage[0]
          : apiMessage || "Erro ao cadastrar síndico"
        Swal.fire("Erro", text, "error")
      })
  }

  const saveEditSindico = async () => {
    if (!selectedSindico?.OID_USUARIO) return

    const { DS_NOME, DS_SOBRENOME, DS_EMAIL } = editSindico

    if (!DS_NOME || !DS_SOBRENOME || !DS_EMAIL) {
      Swal.fire(
        "Atenção",
        "Os campos Nome, Sobrenome e E-mail são obrigatórios.",
        "warning",
      )
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(DS_EMAIL)) {
      Swal.fire("Atenção", "Por favor, insira um e-mail válido.", "warning")
      return
    }

    const lowerEmail = DS_EMAIL.trim().toLowerCase()
    const hasDuplicateEmail = sindicos.some(
      s =>
        s.OID_USUARIO !== selectedSindico.OID_USUARIO &&
        s.DS_EMAIL &&
        s.DS_EMAIL.trim().toLowerCase() === lowerEmail,
    )
    if (hasDuplicateEmail) {
      Swal.fire(
        "Atenção",
        "Já existe um síndico cadastrado com este E-mail.",
        "warning",
      )
      return
    }

    const normalizedCpf = (editSindico.DS_CPF || "").replace(/\D/g, "")
    const normalizedPhone = (editSindico.NR_TELEFONE || "").replace(/\D/g, "")

    const payload = {
      DS_NOME,
      DS_SOBRENOME,
      DS_EMAIL: DS_EMAIL.trim(),
      DS_CPF: normalizedCpf || null,
      NR_TELEFONE: normalizedPhone || null,
      DS_STATUS: 1,
    }

    try {
      await api.put(`/user/${selectedSindico.OID_USUARIO}`, payload)

      Swal.fire("Sucesso", "Síndico atualizado com sucesso", "success")
      setModalEditar(false)
      setPage(1)
      setHasMore(true)
      fetchSindicos()
    } catch (err) {
      const apiMessage = err?.response?.data?.message
      const text = Array.isArray(apiMessage)
        ? apiMessage[0]
        : apiMessage || "Erro ao atualizar síndico"
      Swal.fire("Erro", text, "error")
    }
  }

  const saveNovaSenha = async () => {
    const email = (editSindico.DS_EMAIL || selectedSindico?.DS_EMAIL || "").trim()
    const CLIENT_SECRET = (senhaForm.CLIENT_SECRET || "").trim()
    const CLIENT_SECRET_CONFIRM = (senhaForm.CLIENT_SECRET_CONFIRM || "").trim()

    if (!email) {
      Swal.fire("Atenção", "E-mail do síndico não encontrado.", "warning")
      return
    }

    if (!CLIENT_SECRET) {
      Swal.fire("Atenção", "Informe a nova senha.", "warning")
      return
    }

    if (!CLIENT_SECRET_CONFIRM) {
      Swal.fire("Atenção", "Confirme a nova senha.", "warning")
      return
    }

    if (CLIENT_SECRET !== CLIENT_SECRET_CONFIRM) {
      Swal.fire(
        "Atenção",
        "A confirmação de senha deve ser igual à nova senha.",
        "warning",
      )
      return
    }

    try {
      await api.post("/user/updatesenha", {
        DS_EMAIL: email,
        CLIENT_SECRET,
        CLIENT_SECRET_CONFIRM,
      })
      Swal.fire("Sucesso", "Senha atualizada com sucesso", "success")
      setModalNovaSenha(false)
      setSenhaForm({ CLIENT_SECRET: "", CLIENT_SECRET_CONFIRM: "" })
    } catch (err) {
      const apiMessage = err?.response?.data?.message
      const text = Array.isArray(apiMessage)
        ? apiMessage[0]
        : apiMessage || "Erro ao atualizar a senha"
      Swal.fire("Erro", text, "error")
    }
  }

  const excluirSindico = async sindico => {
    if (!sindico?.OID_USUARIO) return

    const nome = `${sindico.DS_NOME || ""} ${sindico.DS_SOBRENOME || ""}`.trim()
    const condos =
      sindico.condominios && sindico.condominios.length > 0
        ? sindico.condominios
            .filter(c => c && c.NM_RAZAO_SOCIAL)
            .map(c => c.NM_RAZAO_SOCIAL)
            .join(", ")
        : "Nenhum"

    const result = await Swal.fire({
      title: "Excluir síndico?",
      html: `Tem certeza que deseja excluir <strong>${nome || sindico.DS_EMAIL}</strong>?<br><br>Condomínios vinculados serão desvinculados:<br><span class="text-muted">${condos}</span><br><br>Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    })

    if (!result.isConfirmed) return

    try {
      const { data } = await api.delete(`/user/sindicos/${sindico.OID_USUARIO}`)
      Swal.fire(
        "Sucesso",
        data?.message || "Síndico excluído com sucesso",
        "success",
      )
      setPage(1)
      setHasMore(true)
      fetchCondominios()
      fetchSindicos()
    } catch (err) {
      const apiMessage = err?.response?.data?.message
      const text = Array.isArray(apiMessage)
        ? apiMessage[0]
        : apiMessage || "Erro ao excluir síndico"
      Swal.fire("Erro", text, "error")
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title">Gerenciar Síndicos</h4>
                  <Button
                    color="primary"
                    onClick={() => setModalCadastro(true)}
                  >
                    + Cadastrar Síndico
                  </Button>
                </div>

                <Row className="mb-4">
                  <Col md={4}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Pesquisar por nome, email, telefone ou condomínio..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      onKeyDown={handleSearch}
                    />
                  </Col>
                  <Col md={2}>
                    <Button color="secondary" onClick={handleSearch}>
                      Pesquisar
                    </Button>
                  </Col>
                </Row>

                <div className="table-responsive">
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Telefone</th>
                        <th>Condomínios Vinculados</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sindicos.map((s, i) => {
                        const isLast = i === sindicos.length - 1

                        return (
                          <tr
                            key={`${s.OID_USUARIO}-${i}`}
                            ref={isLast ? lastSindicoElementRef : null}
                          >
                            <td>
                              {s.DS_NOME} {s.DS_SOBRENOME}
                            </td>
                            <td>{s.DS_EMAIL}</td>
                            <td>{s.NR_TELEFONE}</td>
                            <td>
                              {s.condominios && s.condominios.length > 0
                                ? s.condominios
                                    .filter(c => c && c.NM_RAZAO_SOCIAL)
                                    .map(c => c.NM_RAZAO_SOCIAL)
                                    .join(", ")
                                : "Nenhum"}
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  color="secondary"
                                  onClick={() => openEditar(s)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  color="info"
                                  onClick={() => openVinculo(s)}
                                >
                                  Vincular Condomínios
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  outline
                                  onClick={() => excluirSindico(s)}
                                >
                                  Excluir
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Mostrando {sindicos.length} de {total} registros
                  </div>
                  {isLoadingMore && (
                    <div className="text-center">
                      <i className="bx bx-loader bx-spin font-size-18"></i>
                      <span className="ms-2">Carregando mais...</span>
                    </div>
                  )}
                  {!hasMore && sindicos.length > 0 && (
                    <div className="text-muted">
                      Todos os registros foram carregados
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={modalVinculo}
        toggle={() => setModalVinculo(!modalVinculo)}
        size="lg"
      >
        <ModalHeader toggle={() => setModalVinculo(!modalVinculo)}>
          Vincular Condomínios - {selectedSindico?.DS_NOME}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label>Selecione os condomínios</label>
            <Select
              isMulti
              options={condominios}
              value={selectedCondos}
              onChange={setSelectedCondos}
              placeholder="Selecione..."
              styles={{
                control: base => ({
                  ...base,
                  backgroundColor: "#fff",
                }),
                menu: base => ({
                  ...base,
                  backgroundColor: "#fff",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#e0dcdc" : "#fff",
                  color: "#000",
                }),
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveVinculos}>
            Salvar
          </Button>
          <Button color="secondary" onClick={() => setModalVinculo(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalCadastro}
        toggle={() => setModalCadastro(!modalCadastro)}
      >
        <ModalHeader toggle={() => setModalCadastro(!modalCadastro)}>
          Cadastrar Novo Síndico
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={novoSindico.DS_NOME}
                  onChange={e =>
                    setNovoSindico({ ...novoSindico, DS_NOME: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Sobrenome <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={novoSindico.DS_SOBRENOME}
                  onChange={e =>
                    setNovoSindico({
                      ...novoSindico,
                      DS_SOBRENOME: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  E-mail <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  value={novoSindico.DS_EMAIL}
                  className="form-control"
                  onChange={e =>
                    setNovoSindico({ ...novoSindico, DS_EMAIL: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>Telefone</label>
                <InputMask
                  mask="(99) 99999-9999"
                  maskChar={null}
                  className="form-control"
                  value={novoSindico.NR_TELEFONE}
                  onChange={e =>
                    setNovoSindico({
                      ...novoSindico,
                      NR_TELEFONE: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Senha <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  value={novoSindico.CLIENT_SECRET}
                  className="form-control"
                  onChange={e =>
                    setNovoSindico({
                      ...novoSindico,
                      CLIENT_SECRET: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Confirmar senha <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  value={novoSindico.CLIENT_SECRET_CONFIRM}
                  className="form-control"
                  onChange={e =>
                    setNovoSindico({
                      ...novoSindico,
                      CLIENT_SECRET_CONFIRM: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>CPF</label>
                <InputMask
                  mask="999.999.999-99"
                  maskChar={null}
                  className="form-control"
                  value={novoSindico.DS_CPF}
                  onChange={e =>
                    setNovoSindico({ ...novoSindico, DS_CPF: e.target.value })
                  }
                />
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveSindico}>
            Cadastrar
          </Button>
          <Button color="secondary" onClick={() => setModalCadastro(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalEditar}
        toggle={() => setModalEditar(!modalEditar)}
      >
        <ModalHeader toggle={() => setModalEditar(!modalEditar)}>
          Editar Síndico
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={editSindico.DS_NOME}
                  onChange={e =>
                    setEditSindico({ ...editSindico, DS_NOME: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  Sobrenome <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={editSindico.DS_SOBRENOME}
                  onChange={e =>
                    setEditSindico({
                      ...editSindico,
                      DS_SOBRENOME: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>
                  E-mail <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  value={editSindico.DS_EMAIL}
                  className="form-control"
                  onChange={e =>
                    setEditSindico({ ...editSindico, DS_EMAIL: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>Telefone</label>
                <InputMask
                  mask="(99) 99999-9999"
                  maskChar={null}
                  className="form-control"
                  value={editSindico.NR_TELEFONE}
                  onChange={e =>
                    setEditSindico({
                      ...editSindico,
                      NR_TELEFONE: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label>CPF</label>
                <InputMask
                  mask="999.999.999-99"
                  maskChar={null}
                  className="form-control"
                  value={editSindico.DS_CPF}
                  onChange={e =>
                    setEditSindico({ ...editSindico, DS_CPF: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md={12}>
              <div className="mb-3">
                <Button color="warning" outline onClick={openNovaSenha}>
                  Nova senha
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveEditSindico}>
            Salvar
          </Button>
          <Button color="secondary" onClick={() => setModalEditar(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalNovaSenha}
        toggle={() => setModalNovaSenha(!modalNovaSenha)}
      >
        <ModalHeader toggle={() => setModalNovaSenha(false)}>
          Nova senha
          {selectedSindico?.DS_NOME
            ? ` — ${selectedSindico.DS_NOME} ${selectedSindico.DS_SOBRENOME || ""}`
            : ""}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <label>
                  Nova senha <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={senhaForm.CLIENT_SECRET}
                  autoComplete="new-password"
                  onChange={e =>
                    setSenhaForm({
                      ...senhaForm,
                      CLIENT_SECRET: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
            <Col md={12}>
              <div className="mb-3">
                <label>
                  Confirmar nova senha <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={senhaForm.CLIENT_SECRET_CONFIRM}
                  autoComplete="new-password"
                  onChange={e =>
                    setSenhaForm({
                      ...senhaForm,
                      CLIENT_SECRET_CONFIRM: e.target.value,
                    })
                  }
                />
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={saveNovaSenha}>
            Alterar senha
          </Button>
          <Button color="secondary" onClick={() => setModalNovaSenha(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default SindicosList
